<template>
  <!-- MYAC-918 / MYAC-1269 — the person's conversations on this project, ONE list
       for both places: the Conversation page's column and the assistant panel's
       « ‹ » (Aymeric, 2026-10-27: same order, same grouping, same words). A new
       one on top, the search once the list is long, the two tabs, then the rows:
       mine by period, the shared ones named to me then project-wide. -->
  <div class="conversation-list" data-testid="conversation-list">
    <button type="button" class="new-btn" data-testid="conversation-new" @click="emit('new')">
      <i class="pi pi-pen-to-square" aria-hidden="true" />{{ t('conversations.new') }}
    </button>
    <label class="search">
      <i class="pi pi-search" aria-hidden="true" />
      <span class="sr-only">{{ t('conversations.search_label') }}</span>
      <input v-model="query" type="search" :placeholder="t('conversations.search_placeholder')" autocomplete="off"
        data-testid="conversation-search" />
    </label>
    <ConversationListTabs class="tabs" />

    <div class="rows">
      <template v-if="tab === 'mine'">
        <!-- A failed read is said, never shown as « none yet ». -->
        <Message v-if="conversations.error" severity="warn" :closable="false" data-testid="conversation-list-error">
          {{ t('conversations.load_failed') }}
        </Message>
        <p v-else-if="conversations.loaded && !recent.length" class="quiet" data-testid="conversation-list-empty">
          {{ t('conversations.empty') }}
        </p>
        <section v-for="g in periods" :key="g.key">
          <!-- A period folds and unfolds by its heading (Aymeric, 2026-10-27). -->
          <h3 class="group-title">
            <button type="button" class="group-toggle" :aria-expanded="!folded('mine', g.key)"
              @click="conversations.toggleGroup(`mine:${g.key}`)">
              <i class="pi" :class="folded('mine', g.key) ? 'pi-chevron-right' : 'pi-chevron-down'" aria-hidden="true" />
              {{ t(`conversations.period.${g.key}`) }}<span class="group-count">{{ g.rows.length }}</span>
            </button>
          </h3>
          <ConversationRow v-for="r in folded('mine', g.key) ? [] : g.rows" :key="r.id" :row="r" :state="conversations.stateOf(r)"
            :wrote-by="conversations.wroteBy(r)" :current="r.id === currentId" right="owner" class="list-row"
            @select="emit('select', r)" @rename="(title: string) => conversations.rename(r.projectId, r.id, title)"
            @share="share(r)" />
        </section>
        <p v-if="recent.length && query.trim() && !filtered.length" class="quiet">
          {{ t('conversations.no_match', { q: query.trim() }) }}
        </p>
      </template>

      <template v-else>
        <Message v-if="conversations.sharedError" severity="warn" :closable="false" data-testid="conversation-shared-error">
          {{ t('conversations.load_failed') }}
        </Message>
        <p v-else-if="!conversations.shared.length" class="quiet" data-testid="conversation-shared-empty">
          {{ t('conversations.shared_empty') }}
        </p>
        <!-- By period too (Aymeric, 2026-10-27); a project-wide one says so on its row. -->
        <section v-for="g in sharedPeriods" :key="g.key" data-testid="conversation-shared-period">
          <!-- A period folds and unfolds by its heading (Aymeric, 2026-10-27). -->
          <h3 class="group-title">
            <button type="button" class="group-toggle" :aria-expanded="!folded('shared', g.key)"
              @click="conversations.toggleGroup(`shared:${g.key}`)">
              <i class="pi" :class="folded('shared', g.key) ? 'pi-chevron-right' : 'pi-chevron-down'" aria-hidden="true" />
              {{ t(`conversations.period.${g.key}`) }}<span class="group-count">{{ g.rows.length }}</span>
            </button>
          </h3>
          <!-- MYAC-1664 — one shared with me to write in, I rename too. -->
          <ConversationRow v-for="r in folded('shared', g.key) ? [] : g.rows" :key="r.id" :row="r" :state="conversations.stateOf(r)"
            :wrote-by="conversations.wroteBy(r)" :current="r.id === currentId" :right="conversations.rightOf(r)" class="list-row"
            @select="emit('select', r)" @rename="(title: string) => conversations.rename(r.projectId, r.id, title)" />
        </section>
        <p v-if="conversations.shared.length && query.trim() && !sharedWithMe.length && !sharedInProject.length" class="quiet">
          {{ t('conversations.no_match', { q: query.trim() }) }}
        </p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Message from 'primevue/message'
import { useConversationsStore, type ConversationRow as Row } from '@/stores/conversations'
import ConversationRow from './ConversationRow.vue'
import ConversationListTabs from './ConversationListTabs.vue'
import { timeGroup, type TimeGroup } from './conversationView'

defineProps<{
  /** The conversation open on screen (highlighted, no label). */
  currentId: string | null
}>()
const emit = defineEmits<{ select: [row: Row]; new: [] }>()

const { t } = useI18n()
const conversations = useConversationsStore()
const query = ref('')

const tab = computed(() => conversations.listTab)
const recent = computed(() => conversations.recent)
const sharedWithMe = computed(() => filterBy(conversations.sharedWithMe))
const sharedInProject = computed(() => filterBy(conversations.sharedInProject))

function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}
function filterBy(rows: Row[]): Row[] {
  const q = norm(query.value.trim())
  return q ? rows.filter((r) => norm(r.title).includes(q)) : rows
}
const filtered = computed(() => filterBy(recent.value))

const PERIODS: TimeGroup[] = ['today', 'week', 'month', 'older']
function byPeriod(rows: Row[]) {
  const now = new Date()
  return PERIODS
    .map((key) => ({ key, rows: rows.filter((r) => timeGroup(r.updatedAt, now) === key) }))
    .filter((g) => g.rows.length)
}
const periods = computed(() => byPeriod(filtered.value))
const sharedPeriods = computed(() =>
  byPeriod([...sharedWithMe.value, ...sharedInProject.value].sort((a, b) => b.updatedAt - a.updatedAt)))

/** Has the person folded this period of this tab? */
function folded(tab: string, key: string): boolean {
  return conversations.foldedGroups[`${tab}:${key}`] === true
}

/** « Partager… » from a row: open that conversation, then its share window. */
function share(row: Row): void {
  emit('select', row)
  conversations.requestShare(row.id)
}
</script>

<style scoped>
/* The Conversation page's column look, kept for both places. */
.conversation-list { display: flex; flex-direction: column; gap: 6px; min-height: 0; height: 100%; }
.new-btn {
  display: flex; align-items: center; gap: 8px; min-height: 40px; padding: 0 10px; border: 1px dashed var(--p-primary-200);
  border-radius: 6px; background: var(--p-primary-50); color: var(--p-primary-color); font: inherit; font-size: 14px; font-weight: 500; cursor: pointer;
}
.new-btn:focus-visible { outline: var(--focus-outline, 2px solid var(--p-primary-color)); outline-offset: 2px; }
.search { display: flex; align-items: center; gap: 8px; height: 36px; padding: 0 10px; border: 1px solid var(--p-surface-200); border-radius: 6px; color: var(--p-surface-500); }
.search input { border: 0; outline: 0; flex: 1; min-width: 0; font: inherit; font-size: 14px; color: var(--p-surface-900); background: transparent; }
.search:focus-within { outline: var(--focus-outline, 2px solid var(--p-primary-color)); outline-offset: 1px; }
.tabs { margin: 0 2px 4px; }
.rows { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; display: flex; flex-direction: column; gap: 1px; margin: 4px -4px 0; scrollbar-width: thin; }
.list-row { padding: 6px 8px; }
.group-toggle {
  display: inline-flex; align-items: center; gap: 6px; padding: 2px 0; border: 0; background: transparent; cursor: pointer;
  font: inherit; letter-spacing: inherit; text-transform: inherit; color: inherit;
}
.group-toggle .pi { font-size: 9px; }
.group-toggle:hover { color: var(--p-surface-700); }
.group-toggle:focus-visible { outline: var(--focus-outline, 2px solid var(--p-primary-color)); outline-offset: 2px; border-radius: 3px; }
.group-count { font-weight: 500; letter-spacing: 0; color: var(--p-surface-400); }
.group-title { margin: 10px 8px 2px; font-size: 11px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--p-surface-500); }
.quiet { margin: 8px; font-size: 13px; line-height: 1.5; color: var(--p-surface-600); }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
</style>
